import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Activity } from '@models/activity.model';
import { useActivityByTask } from '@hooks/api';
import { PaginationActions } from '../index';

interface UseProjectFeedByTaskProps {
  projectId: string;
  taskId: string;
}

export const useProjectFeedByTask = ({
  projectId,
  taskId,
}: UseProjectFeedByTaskProps) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [page, setPage] = useState(1);
  const limit = 10;
  const prevDataRef = useRef<string | null>(null);

  const { data: activitiesByTaskData, isFetching: isFetchingActivitiesByTask } =
    useActivityByTask(
      {
        projectId,
        taskId,
        limit,
        page,
      },
      {
        enabled: Boolean(taskId && projectId),
      },
    );

  const totalCount = useMemo(() => {
    return activitiesByTaskData?.total || 0;
  }, [activitiesByTaskData?.total]);

  const hasMore = useMemo(() => {
    if (!activitiesByTaskData) return false;
    return activities.length < activitiesByTaskData.total;
  }, [activitiesByTaskData, activities.length]);

  // Reset state when projectId or taskId changes
  useEffect(() => {
    setActivities([]);
    setPage(1);
  }, [projectId, taskId]);

  // Accumulate activities
  useEffect(() => {
    if (!activitiesByTaskData?.items || activitiesByTaskData.items.length === 0) {
      return;
    }

    const items = activitiesByTaskData.items;

    // Create a unique signature of the data to detect actual changes
    const dataSignature = JSON.stringify({
      total: activitiesByTaskData.total,
      itemCount: items.length,
      firstId: items[0]?.activityId,
      lastId: items[items.length - 1]?.activityId,
      page: page,
    });

    // Skip if data signature hasn't changed
    if (prevDataRef.current === dataSignature) return;
    prevDataRef.current = dataSignature;

    if (page === 1) {
      setActivities(items ?? []);
    } else if (items && items.length > 0) {
      setActivities((prev) => {
        const existingIds = new Set(prev.map((a: any) => a.activityId));
        const newItems = items.filter(
          (a: any) => !existingIds.has(a.activityId),
        );
        return newItems.length > 0 ? [...prev, ...newItems] : prev;
      });
    }
  }, [activitiesByTaskData, page]);

  const loadMore = useCallback(() => {
    if (isFetchingActivitiesByTask || !hasMore) return;
    setPage((p) => p + 1);
  }, [isFetchingActivitiesByTask, hasMore]);

  const paginationActions: PaginationActions = {
    loadMore,
    hasMore,
    isLoading: isFetchingActivitiesByTask && activities.length === 0,
    isFetchingMore: isFetchingActivitiesByTask && activities.length > 0,
  };

  return {
    activities,
    paginationActions,
    totalCount,
  };
};